import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { ChevronLeft, ChevronRight, Route } from 'lucide-react'
import {
  getCompaniesAtSameAddress,
  getCompanyPersonConnections,
  getEntitySignals,
  type CompanyPersonConnections,
  type SameAddressResult,
} from '../../services/apiClient'
import { Badge, CountryFlagIcon } from '../ui'
import { formatCompanyForm, formatDate, formatStatusLabel, isDissolvedStatus } from '../../utils/format'
import { SAME_ADDRESS_FLAG_THRESHOLD } from './SameAddressCompanies'
import WorthKnowing from './network/WorthKnowing'
import { findings } from './network/networkText'
import type { EntitySignals } from '../../types/network'

const PAGE_SIZE = 25
const PERSON_ROWS_SHOWN = 15

const SECTION_TITLE = 'text-[17px] font-bold text-ink'
const SECTION_DESC = 'mt-0.5 text-sm text-ink-500'
const LIST = 'overflow-hidden rounded-[10px] border border-rule bg-paper divide-y divide-rule-soft'
const PILL =
  'inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-semibold transition-colors'

function pill(active: boolean): string {
  return active
    ? `${PILL} border-ink bg-ink text-paper`
    : `${PILL} border-rule-strong bg-paper text-ink hover:border-ink-300`
}

interface CompanyConnectionsProps {
  cvrNumber: string
  companyName: string
}

/**
 * »Forbindelser« tab on the company page: companies linked through the
 * people involved, every company at the same address, and a way into
 * »Find forbindelse«.
 */
const CompanyConnections: React.FC<CompanyConnectionsProps> = ({ cvrNumber, companyName }) => {
  const location = useLocation()
  const addressRef = useRef<HTMLElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // »N andre på adressen« lands here. The sections load on their own, so the
  // page grows after the first jump: follow it until the reader scrolls.
  useEffect(() => {
    if (location.hash !== '#samme-adresse') return
    const jump = () => addressRef.current?.scrollIntoView({ block: 'start' })
    jump()
    const root = rootRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(jump)
    ro.observe(root)
    const stop = () => ro.disconnect()
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const
    events.forEach((e) => window.addEventListener(e, stop, { once: true, passive: true }))
    const timer = window.setTimeout(stop, 15_000)
    return () => {
      stop()
      window.clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, stop))
    }
  }, [location.hash])

  return (
    <div ref={rootRef} className="space-y-10">
      <CompanySignals cvrNumber={cvrNumber} />
      <PersonLinkedCompanies cvrNumber={cvrNumber} />
      <section id="samme-adresse" ref={addressRef} className="scroll-mt-6" aria-labelledby="samme-adresse-title">
        <SameAddressList cvrNumber={cvrNumber} />
      </section>
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-rule bg-paper px-5 py-4">
        <p className="text-sm text-ink-700">
          <span className="font-semibold text-ink">Leder du efter en bestemt forbindelse?</span>{' '}
          Find den korteste vej fra {companyName} til en person eller et andet selskab.
        </p>
        <Link
          to={`/forbindelse?til=cvr:${cvrNumber}&tilNavn=${encodeURIComponent(companyName)}`}
          className="inline-flex h-[38px] shrink-0 items-center gap-2 rounded-[7px] bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink-700"
        >
          <Route className="h-4 w-4" aria-hidden />
          Find forbindelse
        </Link>
      </section>
    </div>
  )
}

// ── Via personer ────────────────────────────────────────────────────────

const PersonLinkedCompanies: React.FC<{ cvrNumber: string }> = ({ cvrNumber }) => {
  const [data, setData] = useState<CompanyPersonConnections | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    setShowAll(false)
    getCompanyPersonConnections(cvrNumber)
      .then((d) => !cancelled && setData(d))
      .catch((err: unknown) => !cancelled && setError(err instanceof Error ? err.message : 'Fejl'))
    return () => {
      cancelled = true
    }
  }, [cvrNumber])

  const rows = data ? (showAll ? data.companies : data.companies.slice(0, PERSON_ROWS_SHOWN)) : []

  return (
    <section aria-labelledby="via-personer-title" data-testid="company-person-connections">
      <h2 id="via-personer-title" className={SECTION_TITLE}>
        Via personer
        {data && data.companies.length > 0 && (
          <span className="ml-2 text-[13px] font-semibold text-ink-500">{data.companies.length}</span>
        )}
      </h2>
      <p className={SECTION_DESC}>
        Andre aktive selskaber, som ejere, ledelse og bestyrelse også er involveret i. Revisorer er
        ikke med.
      </p>

      <div className="mt-3">
        {!data && !error && <p className="text-sm text-ink-500">Slår personerne op…</p>}
        {error && <p className="text-sm text-signal-700 bg-signal-50 rounded-md px-3 py-2">{error}</p>}
        {data && data.companies.length === 0 && (
          <p className="text-sm text-ink-500">
            {data.personsChecked === 0
              ? 'Ingen personer registreret i selskabet.'
              : 'Ingen af personerne er involveret i andre aktive selskaber.'}
          </p>
        )}
        {data && data.companies.length > 0 && (
          <>
            <ul className={LIST}>
              {rows.map((c) => (
                <li key={c.cvrNummer} className="px-5 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <Link
                      to={`/company/${c.cvrNummer}`}
                      className="inline-flex min-w-0 items-center gap-1.5 font-bold text-ink hover:underline decoration-ink-300 underline-offset-[3px]"
                    >
                      <span className="truncate">{c.navn}</span>
                      {c.countryCode && <CountryFlagIcon code={c.countryCode} focusable={false} />}
                    </Link>
                    <Badge tone={c.persons.length > 1 ? 'neutral' : 'muted'}>
                      {c.persons.length === 1 ? '1 fælles person' : `${c.persons.length} fælles personer`}
                    </Badge>
                  </div>
                  <ul className="mt-1 space-y-0.5 text-[13.5px] text-ink-600">
                    {c.persons.map((p) => (
                      <li key={p.enhedsNummer}>
                        <Link
                          to={`/person/${p.enhedsNummer}`}
                          className="font-semibold text-ink hover:underline decoration-ink-300 underline-offset-[3px]"
                        >
                          {p.navn}
                        </Link>
                        <span className="text-ink-500">
                          {' '}
                          — {p.rolesHere.join(', ')} her · {p.rolesThere.join(', ')} der
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[13px] text-ink-500">
              <span>
                {data.truncated
                  ? `Bygger på de første ${data.personsChecked} af ${data.personsTotal} personer.`
                  : `Bygger på ${data.personsChecked} ${data.personsChecked === 1 ? 'person' : 'personer'}.`}
              </span>
              {data.companies.length > PERSON_ROWS_SHOWN && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="font-semibold text-ink underline decoration-ink-300 underline-offset-[3px] hover:decoration-ink"
                >
                  {showAll ? 'Vis færre' : `Vis alle ${data.companies.length}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

// ── På samme adresse ────────────────────────────────────────────────────

const SameAddressList: React.FC<{ cvrNumber: string }> = ({ cvrNumber }) => {
  const [result, setResult] = useState<SameAddressResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [includeDissolved, setIncludeDissolved] = useState(false)
  const [sameUnit, setSameUnit] = useState(false)
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')

  useEffect(() => {
    const id = window.setTimeout(() => {
      setQ(draft.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [draft])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getCompaniesAtSameAddress(cvrNumber, { page, pageSize: PAGE_SIZE, includeDissolved, sameUnit, q })
      .then((r) => !cancelled && setResult(r))
      .catch((err: unknown) => !cancelled && setError(err instanceof Error ? err.message : 'Fejl'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [cvrNumber, page, includeDissolved, sameUnit, q])

  const address = result?.address.replace(/,?\s*(?:Country|Land):\s*DK\b/i, '') ?? ''
  const flagged = !!result && result.activeCount >= SAME_ADDRESS_FLAG_THRESHOLD
  const maxPage = result ? Math.max(1, Math.ceil(result.filteredCount / PAGE_SIZE)) : 1
  const from = result && result.filteredCount > 0 ? (page - 1) * PAGE_SIZE + 1 : 0
  const to = result ? Math.min(page * PAGE_SIZE, result.filteredCount) : 0

  if (result && !result.address) {
    return (
      <>
        <h2 id="samme-adresse-title" className={SECTION_TITLE}>På samme adresse</h2>
        <p className={SECTION_DESC}>Adressen er for upræcis til at finde andre selskaber på den.</p>
      </>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <h2 id="samme-adresse-title" className={SECTION_TITLE}>På samme adresse</h2>
          <p className={SECTION_DESC}>
            {address ? `${address} · samme bygning, alle etager. Nyeste først.` : 'Henter adressen…'}
          </p>
        </div>
        {result && (
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{result.totalCount} i alt</Badge>
            <Badge tone={flagged ? 'signal' : 'neutral'}>{result.activeCount} {result.activeCount === 1 ? 'aktivt' : 'aktive'}</Badge>
            {result.unitCount !== null && result.unit && (
              <Badge tone="neutral">
                {result.unitCount} på {result.unit}
              </Badge>
            )}
          </div>
        )}
      </div>

      {result && result.totalCount > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Filtrér">
          <button
            type="button"
            aria-pressed={!includeDissolved}
            onClick={() => {
              setIncludeDissolved(false)
              setPage(1)
            }}
            className={pill(!includeDissolved)}
          >
            Aktive
          </button>
          <button
            type="button"
            aria-pressed={includeDissolved}
            onClick={() => {
              setIncludeDissolved(true)
              setPage(1)
            }}
            className={pill(includeDissolved)}
          >
            Alle, også ophørte
          </button>
          {result.unit && (
            <button
              type="button"
              aria-pressed={sameUnit}
              onClick={() => {
                setSameUnit((v) => !v)
                setPage(1)
              }}
              className={pill(sameUnit)}
            >
              Kun {result.unit}
            </button>
          )}
          {result.totalCount > 10 && (
            <input
              type="search"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Find selskab på adressen"
              aria-label="Find selskab på adressen"
              className="ml-auto h-8 w-full rounded-md border border-rule-strong bg-paper px-2.5 text-sm text-ink placeholder:text-ink-400 sm:w-64"
            />
          )}
        </div>
      )}

      <div className="mt-3">
        {error && <p className="text-sm text-signal-700 bg-signal-50 rounded-md px-3 py-2">{error}</p>}
        {!error && !result && <p className="text-sm text-ink-500">Henter…</p>}
        {result && result.totalCount === 0 && (
          <p className="text-sm text-ink-500">Ingen andre selskaber er registreret på adressen.</p>
        )}
        {result && result.totalCount > 0 && result.filteredCount === 0 && (
          <p className="text-sm text-ink-500">Ingen selskaber matcher filtrene.</p>
        )}
        {result && result.companies.length > 0 && (
          <ul className={`${LIST} ${loading ? 'opacity-60' : ''}`} data-testid="same-address-list">
            {result.companies.map((c) => {
              const gone = !!c.status && isDissolvedStatus(c.status)
              const founded = formatDate(c.startdato)
              return (
                <li key={c.cvrNummer}>
                  <Link to={`/company/${c.cvrNummer}`} className="block px-5 py-2.5 hover:bg-[#FAFBFC]">
                    <span className={`flex items-center gap-2 font-semibold ${gone ? 'text-ink-500' : 'text-ink'}`}>
                      <span className="truncate">{c.navn || `CVR ${c.cvrNummer}`}</span>
                      {gone && <Badge tone="muted">{formatStatusLabel(c.status)}</Badge>}
                    </span>
                    <span className="mt-0.5 block text-[13px] text-ink-500 tabular-nums">
                      {[formatCompanyForm(c.virksomhedsform), `CVR ${c.cvrNummer}`, founded && `stiftet ${founded}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
        {result && maxPage > 1 && (
          <nav className="mt-3 flex flex-wrap items-center justify-between gap-3" aria-label="Sideinddeling">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Forrige
            </button>
            <span className="text-[13px] text-ink-500 tabular-nums">
              Viser {from}–{to} af {result.filteredCount}
            </span>
            <button
              type="button"
              disabled={page >= maxPage || loading}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300 disabled:opacity-40"
            >
              Næste
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </nav>
        )}
      </div>
    </>
  )
}

/** Sanctions and Offshore Leaks hits for the company's name; nothing when clean. */
function CompanySignals({ cvrNumber }: { cvrNumber: string }) {
  const [signals, setSignals] = useState<EntitySignals | null>(null)
  useEffect(() => {
    let cancelled = false
    setSignals(null)
    getEntitySignals('company', cvrNumber)
      .then((s) => !cancelled && setSignals(s))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [cvrNumber])
  const items = findings(signals, [])
  return items.length > 0 ? <WorthKnowing items={items} /> : null
}

export default CompanyConnections
